import { useState } from "react";
import type { Catalog } from "./api";
import { Badge, Empty, Icon, PageTitle, dateTime } from "./components";

type Place = Catalog["places"][number];
type Event = Catalog["events"][number];
type Space = Catalog["spaces"][number];
import type { ReservationDraft } from "./view-models";
export { emptyReservation } from "./view-models";

export function PlaceFilter({
  catalog,
  placeId,
  onChange,
}: {
  catalog: Catalog;
  placeId: string;
  onChange: (id: string) => void;
}) {
  return (
    <label className="place-filter">
      <Icon name="places" />
      <span className="sr-only">Filtrer par lieu</span>
      <select value={placeId} onChange={(e) => onChange(e.target.value)}>
        <option value="">Tous les lieux</option>
        {catalog.places.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </select>
    </label>
  );
}
function Facts({ rows }: { rows: Array<[string, string]> }) {
  return (
    <dl className="facts">
      {rows.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
function DemoNote({ demo }: { demo: boolean }) {
  return demo ? (
    <p className="note">
      Catalogue synthétique de développement. Les informations présentées
      servent à explorer les parcours.
    </p>
  ) : null;
}
export function AgendaScreen({
  catalog,
  placeId,
  onPlaceChange,
  onPlace,
}: {
  catalog: Catalog;
  placeId: string;
  onPlaceChange: (id: string) => void;
  onPlace?: (id: string) => void;
}) {
  const [view, setView] = useState<"program" | "mine">("program");
  const [eventId, setEventId] = useState<string | null>(null);
  const event = catalog.events.find((e) => e.id === eventId) as
    | Event
    | undefined;
  const events = catalog.events.filter(
    (e) => !placeId || e.place_id === placeId,
  );
  if (event) {
    const place = catalog.places.find((p) => p.id === event.place_id);
    return (
      <>
        <button
          className="text-button back-button"
          onClick={() => setEventId(null)}
        >
          ← Retour à l’agenda
        </button>
        <PageTitle eyebrow="Se retrouver" title={event.title}>
          {event.description ||
            "Le descriptif de cette activité sera précisé avant l’ouverture des inscriptions."}
        </PageTitle>
        <DemoNote demo={catalog.demo} />
        <section className="panel">
          <Badge>Inscription prochainement</Badge>
          <Facts
            rows={[
              [
                "Quand",
                dateTime(event.starts_at) +
                  (event.ends_at
                    ? ` · jusqu’à ${dateTime(event.ends_at)}`
                    : " · fin à confirmer"),
              ],
              ["Où", place?.name || "Lieu à confirmer"],
              [
                "Format",
                event.format === "remote"
                  ? "À distance"
                  : event.format === "hybrid"
                    ? "Sur place et à distance"
                    : event.format === "onsite"
                      ? "Sur place"
                      : "À confirmer",
              ],
              [
                "Pour se retrouver",
                event.joining_details ||
                  "Les modalités seront précisées avant l’ouverture des inscriptions.",
              ],
            ]}
          />
          {place && onPlace && (
            <button className="text-button" onClick={() => onPlace(place.id)}>
              Découvrir ce lieu <Icon name="arrow" />
            </button>
          )}
        </section>
        <section className="panel">
          <h2>Participer à cette rencontre</h2>
          <p className="muted">
            Les inscriptions, les capacités et les conditions d’annulation ne
            sont pas encore raccordées. Consulter cette fiche ne crée aucun
            engagement.
          </p>
          <button className="button full" disabled>
            Inscriptions indisponibles
          </button>
        </section>
      </>
    );
  }
  return (
    <>
      <PageTitle eyebrow="Se retrouver" title="Agenda">
        Un même agenda, des occasions de se rencontrer partout.
      </PageTitle>
      <div className="segments" aria-label="Vue de l’agenda">
        <button
          aria-pressed={view === "program"}
          onClick={() => setView("program")}
        >
          Programmation
        </button>
        <button aria-pressed={view === "mine"} onClick={() => setView("mine")}>
          Mon agenda
        </button>
      </div>
      {view === "mine" ? (
        <>
          <section className="panel">
            <h2>Mes engagements dans tout Avalon</h2>
            <p className="muted">
              Vos inscriptions, réservations et rendez-vous confirmés seront
              réunis ici, quel que soit le lieu exploré.
            </p>
            <Empty icon="agenda" title="Aucun engagement relié pour le moment.">
              Ces services ne sont pas encore ouverts. Un accord de mise en
              relation n’est pas un rendez-vous confirmé.
            </Empty>
          </section>
          <button className="button" onClick={() => setView("program")}>
            Explorer la programmation <Icon name="arrow" />
          </button>
        </>
      ) : (
        <>
          <PlaceFilter
            catalog={catalog}
            placeId={placeId}
            onChange={onPlaceChange}
          />
          {catalog.demo && (
            <p className="note">
              Programmation de démonstration. Les inscriptions ne sont pas
              encore ouvertes.
            </p>
          )}
          <div className="stack">
            {events.map((e) => (
              <article className="panel event-row" key={e.id}>
                <div className="date-tile">
                  <strong>{new Date(e.starts_at).getDate()}</strong>
                  <span>
                    {new Date(e.starts_at).toLocaleString("fr-FR", {
                      month: "short",
                    })}
                  </span>
                </div>
                <div>
                  <span className="eyebrow">
                    {catalog.places.find((p) => p.id === e.place_id)?.name ||
                      "Avalon"}
                  </span>
                  <h2>{e.title}</h2>
                  <p className="muted">{dateTime(e.starts_at)}</p>
                  <Badge>Inscription prochainement</Badge>
                  <button
                    className="text-button full event-detail-button"
                    onClick={() => setEventId(e.id)}
                  >
                    Découvrir l’événement <Icon name="arrow" />
                  </button>
                </div>
              </article>
            ))}
          </div>
          {!events.length && (
            <Empty icon="agenda" title="Un peu de calme ici.">
              Aucun événement dans cette sélection.
            </Empty>
          )}
        </>
      )}
    </>
  );
}
export function PlacesScreen({
  catalog,
  selectedId,
  onSelect,
  onExplore,
}: {
  catalog: Catalog;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onExplore: (id: string, target: "agenda" | "reserve" | "guild") => void;
}) {
  const place = catalog.places.find((p) => p.id === selectedId) as
    | Place
    | undefined;
  if (place) {
    const spaces = catalog.spaces.filter(
      (s) => s.place_id === place.id,
    ) as Space[];
    return (
      <>
        <button
          className="text-button back-button"
          onClick={() => onSelect(null)}
        >
          ← Tous les lieux
        </button>
        <PageTitle eyebrow="Un point de rencontre" title={place.name}>
          {place.description}
        </PageTitle>
        <DemoNote demo={catalog.demo} />
        <section className="panel">
          <h2>Préparer ma venue</h2>
          <Facts
            rows={[
              ["Adresse", place.address || "Adresse pratique à confirmer"],
              [
                "Horaires",
                place.opening_hours || "Horaires à confirmer auprès du lieu",
              ],
              [
                "Conditions d’accès",
                place.access_conditions ||
                  "Conditions d’utilisation à préciser avant votre venue",
              ],
            ]}
          />
        </section>
        <section className="panel">
          <h2>Ce que je peux faire ici</h2>
          {place.equipment?.length ? (
            <div className="tag-row">
              {place.equipment.map((item) => (
                <Badge key={item}>{item}</Badge>
              ))}
            </div>
          ) : (
            <p className="muted">
              Les équipements et les services du lieu seront précisés dans son
              catalogue.
            </p>
          )}
          <p className="fineprint">
            La présence d’un équipement ne garantit pas sa disponibilité.
          </p>
        </section>
        <div className="stack">
          <button
            className="button primary full"
            onClick={() => onExplore(place.id, "agenda")}
          >
            <Icon name="agenda" />
            Agenda du lieu <Icon name="arrow" />
          </button>
          <button
            className="button full"
            onClick={() => onExplore(place.id, "reserve")}
          >
            <Icon name="places" />
            Espaces à réserver <Icon name="arrow" />
          </button>
          <button
            className="button full"
            onClick={() => onExplore(place.id, "guild")}
          >
            <Icon name="guild" />
            Rencontrer la Guilde ici <Icon name="arrow" />
          </button>
        </div>
        <section>
          <div className="section-heading">
            <h2>Les espaces du lieu</h2>
            <Badge>
              {spaces.length
                ? `${spaces.length} aperçu${spaces.length > 1 ? "s" : ""}`
                : "Catalogue à venir"}
            </Badge>
          </div>
          {spaces.length ? (
            spaces.map((space) => (
              <article className="panel" key={space.id}>
                <h3>{space.name}</h3>
                <p className="muted">
                  {space.capacity
                    ? `Jusqu’à ${space.capacity} personnes · `
                    : ""}
                  {space.equipment?.join(" · ") || "Équipements à confirmer"}
                </p>
                <button
                  className="text-button"
                  onClick={() => onExplore(place.id, "reserve")}
                >
                  Préparer une réservation <Icon name="arrow" />
                </button>
              </article>
            ))
          ) : (
            <p className="muted">
              Les espaces réservables et leurs conditions ne sont pas encore
              publiés.
            </p>
          )}
        </section>
      </>
    );
  }
  return (
    <>
      <PageTitle eyebrow="Le réseau prend place" title="Nos lieux">
        Des points de rencontre. Et tout ce qu’on peut y faire ensemble.
      </PageTitle>
      <DemoNote demo={catalog.demo} />
      <div className="stack">
        {catalog.places.map((p, index) => (
          <article className="place-card" key={p.id}>
            <div
              className={`place-art place-art-${index % 2}`}
              aria-hidden="true"
            >
              <span className="architecture-arch" />
              <span className="architecture-circle" />
              <span className="art-label">
                AVALON / {String(index + 1).padStart(2, "0")}
              </span>
            </div>
            <div className="place-content">
              <div className="section-heading">
                <h2>{p.name}</h2>
                <Icon name="places" />
              </div>
              <p className="muted">{p.description}</p>
              <div className="button-row">
                <button
                  className="button primary"
                  onClick={() => onSelect(p.id)}
                >
                  Découvrir le lieu <Icon name="arrow" />
                </button>
                <button
                  className="text-button"
                  onClick={() => onExplore(p.id, "agenda")}
                >
                  Voir l’agenda
                </button>
                <button
                  className="text-button"
                  onClick={() => onExplore(p.id, "reserve")}
                >
                  Les espaces
                </button>
              </div>
            </div>
          </article>
        ))}
      </div>
      {!catalog.places.length && (
        <Empty icon="places" title="Le catalogue se prépare.">
          Les lieux seront présentés ici dès leur publication.
        </Empty>
      )}
    </>
  );
}
export function ReserveScreen({
  catalog,
  placeId,
  onPlaceChange,
  draft,
  onDraft,
}: {
  catalog: Catalog;
  placeId: string;
  onPlaceChange: (id: string) => void;
  draft: ReservationDraft;
  onDraft: (draft: ReservationDraft) => void;
}) {
  const [view, setView] = useState<"search" | "mine">("search");
  const [summary, setSummary] = useState(false);
  const spaces = catalog.spaces.filter(
    (s) => !placeId || s.place_id === placeId,
  ) as Space[];
  const selected = spaces.find((s) => s.id === draft.space_id);
  return (
    <>
      <PageTitle eyebrow="Faire de la place" title="Réserver">
        Un espace pour travailler, créer ou se retrouver.
      </PageTitle>
      <div className="segments" aria-label="Vues des réservations">
        <button
          aria-pressed={view === "search"}
          onClick={() => setView("search")}
        >
          Trouver un espace
        </button>
        <button aria-pressed={view === "mine"} onClick={() => setView("mine")}>
          Mes réservations
        </button>
      </div>
      {view === "mine" ? (
        <Empty icon="places" title="Aucune réservation reliée.">
          Les demandes et réservations de tous les lieux apparaîtront ici quand
          le service sera ouvert.
        </Empty>
      ) : (
        <>
          <PlaceFilter
            catalog={catalog}
            placeId={placeId}
            onChange={(id) => {
              onPlaceChange(id);
              onDraft({ ...draft, space_id: "" });
              setSummary(false);
            }}
          />
          <p className="note">
            Les disponibilités et conditions des espaces ne sont pas encore
            reliées. Aucune réservation n’est créée depuis cette version.
          </p>
          <div className="stack">
            {spaces.map((space) => (
              <article className="panel" key={space.id}>
                <div className="section-heading">
                  <h2>{space.name}</h2>
                  <Badge>Aperçu</Badge>
                </div>
                <p className="muted">
                  {catalog.places.find((p) => p.id === space.place_id)?.name}
                </p>
                <Facts
                  rows={[
                    [
                      "Capacité",
                      space.capacity
                        ? `Jusqu’à ${space.capacity} personnes`
                        : "À confirmer",
                    ],
                    [
                      "Équipements",
                      space.equipment?.join(", ") || "À confirmer",
                    ],
                    [
                      "Conditions",
                      space.conditions || "À préciser avant toute réservation",
                    ],
                  ]}
                />
                <button
                  className="button"
                  aria-pressed={draft.space_id === space.id}
                  onClick={() => {
                    onDraft({ ...draft, space_id: space.id });
                    setSummary(false);
                  }}
                >
                  {draft.space_id === space.id
                    ? "Espace sélectionné"
                    : "Choisir cet aperçu"}
                </button>
              </article>
            ))}
          </div>
          {!spaces.length && (
            <Empty icon="places" title="Les réservations arrivent.">
              Aucun espace réservant des créneaux réels n’est publié dans cette
              sélection.
            </Empty>
          )}
          <form
            className="panel form-stack"
            onSubmit={(e) => {
              e.preventDefault();
              setSummary(true);
            }}
          >
            <div className="section-heading">
              <h2>Préparer mon besoin d’espace</h2>
              <Badge>Brouillon</Badge>
            </div>
            <p className="muted">
              Vous pouvez préciser votre recherche. Ce brouillon reste dans
              cette session et n’est transmis à aucun lieu.
            </p>
            <label>
              Pour quel usage ?
              <input
                maxLength={160}
                value={draft.purpose}
                onChange={(e) => {
                  onDraft({ ...draft, purpose: e.target.value });
                  setSummary(false);
                }}
                placeholder="Travailler au calme, créer à plusieurs…"
              />
            </label>
            <div className="form-columns">
              <label>
                Date souhaitée
                <input
                  type="date"
                  value={draft.date}
                  onChange={(e) => onDraft({ ...draft, date: e.target.value })}
                />
              </label>
              <label>
                Heure souhaitée
                <input
                  type="time"
                  value={draft.time}
                  onChange={(e) => onDraft({ ...draft, time: e.target.value })}
                />
              </label>
            </div>
            <div className="form-columns">
              <label>
                Durée
                <select
                  value={draft.duration}
                  onChange={(e) =>
                    onDraft({ ...draft, duration: e.target.value })
                  }
                >
                  <option value="30">30 minutes</option>
                  <option value="60">1 heure</option>
                  <option value="120">2 heures</option>
                  <option value="240">4 heures</option>
                </select>
              </label>
              <label>
                Nombre de personnes
                <input
                  type="number"
                  min="1"
                  max="1000"
                  value={draft.people}
                  onChange={(e) =>
                    onDraft({ ...draft, people: e.target.value })
                  }
                />
              </label>
            </div>
            <button className="button" type="submit">
              Voir mon récapitulatif <Icon name="arrow" />
            </button>
            {summary && (
              <div className="booking-summary" role="status">
                <h3>Votre recherche est prête</h3>
                <Facts
                  rows={[
                    [
                      "Lieu / espace",
                      selected?.name ||
                        catalog.places.find((p) => p.id === placeId)?.name ||
                        "Tous les lieux",
                    ],
                    ["Usage", draft.purpose || "À préciser"],
                    [
                      "Quand",
                      `${draft.date || "Date à préciser"} · ${draft.time || "Heure à préciser"}`,
                    ],
                    [
                      "Durée et groupe",
                      `${draft.duration} minutes · ${draft.people || "—"} personne(s)`,
                    ],
                  ]}
                />
                <p>
                  Ce récapitulatif ne constitue ni une demande envoyée, ni une
                  réservation confirmée.
                </p>
                <button className="button full" type="button" disabled>
                  Réservation indisponible
                </button>
              </div>
            )}
          </form>
        </>
      )}
      <p className="fineprint">
        Accepter une mise en relation ne réserve jamais un espace.
      </p>
    </>
  );
}

export { CardScreen } from "./Membership";
